import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { FormGroup, FormArray, AbstractControl, FormControl } from '@angular/forms';
import { SessionStorage, IApiAddress } from '@bhtp-client-api/base';
import { State, SelectItem, BhtpJurisdictionsService, AddressSearchResult, AddressValidState } from '@bhtp-client-api/eligibility';
import { AddressViewConfig, PolicyViewConfig, TravelerDetailViewConfig, TypeaheadHelper, ModalService } from '@bhtp-client-web/component';
import { Observable } from 'rxjs';

import { NavigationStepComponent } from './navigation-step.component';
import { PurchaseNavigationService } from './purchase-navigation.service';
import { PurchaseData, PrimaryTravelerFieldConfig, Traveler, TripDetails, Address } from './../shared/models';
import { FeatureService } from './../shared/services';
import { WriteValueOverride } from './models';
import { PolicyBuyerComponent } from './policy-buyer/policy-buyer.component';
import { AddressResult } from '../shared/models/address-result.model';

@Component({
    selector: 'finish-quote-step',
    template: require('./finish-quote-step.component.html')
})
export class FinishQuoteStepComponent extends NavigationStepComponent implements OnInit {
    @Input() public states: State[];
    @Output() public tripCostChanged: EventEmitter<any> = new EventEmitter<any>();

    public get isPolicyBuyerPostalInvalid(): boolean {
        return Boolean(this.policyBuyer && this.policyBuyerAddressResult && this.policyBuyerAddressResult.validState === AddressValidState.Invalid);
    }

    public get isPolicyHolderPostalInvalid(): boolean {
        return Boolean(this.policyHolderAddressResult && this.policyHolderAddressResult.validState === AddressValidState.Invalid);
    }

    public get policyBuyerPostalError(): string {
        return (this.policyBuyerAddressResult && this.policyBuyerAddressResult.errorMessage) || '';
    }

    public get policyHolderPostalError(): string {
        return (this.policyHolderAddressResult && this.policyHolderAddressResult.errorMessage) || '';
    }

    public policyHolder: Traveler;
    public policyBuyer: Traveler;
    public additionalTravelers: Traveler[];
    public tripDetails: TripDetails;

    public policyHolderForm: FormGroup;
    public policyBuyerForm: FormGroup;
    public additionalTravelerForms: FormArray;
    public tripDetailsForm: FormGroup;

    public policyHolderConfig: PrimaryTravelerFieldConfig;
    public policyBuyerConfig: PrimaryTravelerFieldConfig;
    public additionalTravelerConfig: TravelerDetailViewConfig;
    public policyConfig: PolicyViewConfig;
    public maxTravelers: number;

    public get showAdditionalTravelers(): boolean {
        return this.currentPurchaseData.additionalTravelers ? this.currentPurchaseData.additionalTravelers.length > 0 : false;
    }

    private sessionKeyPrefix: string = 'FinishQuote';
    private policyHolderSessionKey: string = `${ this.sessionKeyPrefix }-PolicyHolder-${ this.currentPurchaseData.sessionId }`;
    private policyBuyerSessionKey: string = `${ this.sessionKeyPrefix }-PolicyBuyer-${ this.currentPurchaseData.sessionId }`;
    private additionalTravelerSessionKey: string = `${this.sessionKeyPrefix}-AdditionalTravelers-${this.currentPurchaseData.sessionId}`;

    private requiresQuoteBeforeContinue: boolean = false;
    private policyBuyerAddressResult: AddressResult;
    private policyHolderAddressResult: AddressResult;

    constructor(private sessionStorage: SessionStorage, private features: FeatureService, private jurisdictionsService: BhtpJurisdictionsService, private modalService: ModalService, navigationService: PurchaseNavigationService) {
        super(navigationService);

        this.policyHolder = this.sessionStorage.getOneTimeValue<Traveler>(this.policyHolderSessionKey) || this.copyValue(this.currentPurchaseData.primaryTraveler);
        this.policyBuyer = this.sessionStorage.getOneTimeValue<Traveler>(this.policyBuyerSessionKey) || this.copyValue(this.currentPurchaseData.policyBuyer);
        this.additionalTravelers = this.sessionStorage.getOneTimeValue<Traveler[]>(this.additionalTravelerSessionKey) || this.copyValue(this.currentPurchaseData.additionalTravelers);
        this.tripDetails = TripDetails.fromPurchaseData(this.currentPurchaseData);
    }

    public ngOnInit(): void {
        this.maxTravelers = this.currentPackage.configuration.maximumNumberTravelers;
    }

    public canContinue(): Observable<boolean> {
        return Observable.of(
            this.policyHolderForm.valid &&
            this.policyBuyerForm.valid &&
            (this.tripDetailsForm.valid || this.tripDetailsForm.disabled) &&
            this.additionalTravelerForms.valid &&
            (!this.policyBuyer || (this.policyBuyerAddressResult !== null && this.policyBuyerAddressResult.validState === AddressValidState.Valid))
        );
    }

    public writeValues(purchaseData: PurchaseData, override: WriteValueOverride = WriteValueOverride.Default): void {
        this.writePolicyHolderValues(purchaseData, override);
        this.writePolicyBuyerValues(purchaseData, override);
        this.writeAdditionalTravelerValues(purchaseData, override);
        this.writeTripDetails(purchaseData);
    }

    public buildForm(): void {
        this.policyHolderForm = new FormGroup({ });
        this.policyBuyerForm = new FormGroup({ });

        this.policyBuyerAddressResult = new AddressResult();
        this.policyHolderAddressResult = new AddressResult();

        this.additionalTravelerForms = new FormArray([]);
        if (this.currentPurchaseData.additionalTravelers) {
            this.currentPurchaseData.additionalTravelers.forEach(() => {
                this.additionalTravelerForms.push(new FormGroup({ }));
            });
        }

        this.tripDetailsForm = new FormGroup({ });

        this.setupFormChangesSubscriber();
        this.setupPolicyHolderConfig();
        this.setupPolicyBuyerConfig();
        this.setupAdditionalTravelerConfig();
        this.setupPolicyConfig();
    }

    public quoteBeforeContinue(): boolean {
        return this.requiresQuoteBeforeContinue;
    }

    public onPurchaseForOtherChanged(isPurchaseForOther: boolean): void {
        this.currentPurchaseData.purchaseForOther = isPurchaseForOther;

        if (this.currentPurchaseData.purchaseForOther) {
            this.policyBuyer = new Traveler(false);
        }
        else {
            this.policyBuyer = null;
        }

        this.updateAddressResult(this.policyBuyerAddressResult, AddressValidState.Unverified, null, null);
    }

    private setupFormChangesSubscriber(): void {
        this.setUpAddressAndValidationForForm(this.policyBuyerForm, false);
        this.setUpAddressAndValidationForForm(this.policyHolderForm, true);

        this.additionalTravelerForms.valueChanges
            .debounceTime(1000)
            .subscribe(
                (formValues: Traveler[]) => {
                    if (formValues) {
                        this.reQuoteOnAdditionalTravelerChange(formValues);
                    }
                },
                (error: Error) => this.onValueChangesError(error));
    }

    private onValueChangesError(error: Error): void {
        // This should never happen, in the ng2 source error is never emitted
        this.requiresQuoteBeforeContinue = true;
        console.error('Failed to requote for trip cost changes');
        console.error(error);
    }

    /**
     * Sets up subscriptions for form validation, intended for Policy Buyer
     * and Policy Holder forms.
     * Both must have valid city + postal + state combinations, but
     * Policy Holder must have the same state as selected in the initial page,
     * which was used to load package rules.
     * A change to the policy holder data also triggers a requote on the policy.
     * @param form The form to which we are subscribing.
     * @param isPolicyHolder True if the additional policy holder rules should be configured.
     */
    private setUpAddressAndValidationForForm(form: FormGroup, isPolicyHolder: boolean): void {
        const addressResult: AddressResult = isPolicyHolder ? this.policyHolderAddressResult : this.policyBuyerAddressResult;

        // do stuff immediately
        form.valueChanges
            .subscribe((formValues: Traveler) => {
                if (addressResult && addressResult.result &&
                        formValues && formValues.address &&
                        addressResult.result.postalCode === formValues.address.postalCode) {
                    // no need to do anything. we have a valid result being set into the form.
                    return;
                }

                // the value being set is different from what we previously verified (or didn't verify, yet).
                this.updateAddressResult(addressResult, AddressValidState.Unverified, null, null);
            });

        // do stuff later
        form.valueChanges
            .debounceTime(1000)
            .subscribe((formValues: Traveler) => {
                const postalCodeControl: AbstractControl = form && form.controls['address'] &&
                    (<FormGroup>form.controls['address']).controls['postalCode'];

                // verify address.
                this.jurisdictionsService.searchResidenceByPostalCode(postalCodeControl.value, 'US')
                    .subscribe(result => {
                        if(!result || result.validState !== AddressValidState.Valid) {
                            // show a modal? invalid postal code.
                            this.updateAddressResult(addressResult, AddressValidState.Invalid, null, this.jurisdictionsService.invalidPostalCodeErrorMessage);
                            this.modalService.showMessageModal('Invalid Postal Code', this.jurisdictionsService.invalidPostalCodeErrorMessage);

                            return;
                        }

                        if (isPolicyHolder && result.result.stateOrProvince !== this.currentPurchaseData.residenceState) {
                            // error - show a modal? state mismatch.
                            this.updateAddressResult(addressResult, AddressValidState.Invalid, null, this.jurisdictionsService.postalStateMismatchErrorMessage);
                            this.modalService.showMessageModal('Postal Code Mismatch', this.jurisdictionsService.postalStateMismatchErrorMessage);

                            return;
                        }

                        this.updateAddressResult(addressResult, result.validState, result.result, null);
                        form.patchValue({ address: { city: result.result.city, stateOrProvince: result.result.stateOrProvince } }, { emitEvent: false, onlySelf: true });

                        if (isPolicyHolder) {
                            // only need to requote if the address of the policy holder / primary traveler is updated.
                            this.reQuoteOnPolicyHolderTripCostChange(formValues.tripCost);
                        }
                    });
                },
                (error: Error) => this.onValueChangesError(error));
    }

    private updateAddressResult(toUpdate: AddressResult, state: AddressValidState, result: IApiAddress, errorMessage: string): void {
        if (!toUpdate) {
            this.modalService.showMessageModal('Development Error', 'Expected an address result to update. This won\'t work without one.');

            return;
        }

        toUpdate.validState = state;
        toUpdate.result = result;
        toUpdate.errorMessage = errorMessage;
    }

    private setupPolicyConfig(): void {
        if (this.fieldConfigs) {
            this.policyConfig = JSON.parse(JSON.stringify(this.fieldConfigs.policy));
            this.policyConfig.tripDates.show = false;

            // if airline isn't required, then don't show any providers. Providers are editable because consumer
            // site collects them regardless of the product
            if (this.policyConfig.airline && this.policyConfig.airline.editable && !this.policyConfig.airline.required) {
                this.policyConfig.airline.show = false;
                this.policyConfig.carRentalCompany.show = false;
                this.policyConfig.cruiseLine.show = false;
                this.policyConfig.rentalCompany.show = false;
                this.policyConfig.hotel.show = false;
                this.policyConfig.tourOperator.show = false;
            }
        }
    }

    private setupPolicyHolderConfig(): void {
        if (this.fieldConfigs) {
            this.policyHolderConfig = JSON.parse(JSON.stringify(this.fieldConfigs.policyHolder));
            this.policyHolderConfig.addressConfig = new AddressViewConfig();
            this.policyHolderConfig.addressConfig.address = this.policyHolderConfig.address;
            this.policyHolderConfig.addressConfig.stateOrProvinceEnabled = false;
            this.policyHolderConfig.birthDate.editable = false;
            this.policyHolderConfig.age.show = true;
            if (this.policyHolderConfig.gender.required) {
                this.policyHolderConfig.gender.show = true;
                this.policyHolderConfig.gender.editable = true;
            }
        }
    }

    private setupPolicyBuyerConfig(): void {
        if (this.fieldConfigs) {
            this.policyBuyerConfig = JSON.parse(JSON.stringify(this.fieldConfigs.policyHolder));
            this.policyBuyerConfig.addressConfig = new AddressViewConfig;
            this.policyBuyerConfig.addressConfig.address = this.policyBuyerConfig.address;
            this.policyBuyerConfig.addressConfig.address.required = false;
            this.policyBuyerConfig.addressConfig.stateOrProvinceEnabled = false;
            this.policyBuyerConfig.birthDate.required = false;
            this.policyBuyerConfig.phoneNumber.required = false;

            if (this.currentPurchaseData.primaryTraveler.emailGroup.noEmail) {
                this.policyBuyerConfig.email.required = false;
            }
        }
    }

    private setupAdditionalTravelerConfig(): void {
        if (this.fieldConfigs) {
            this.additionalTravelerConfig = JSON.parse(JSON.stringify(this.fieldConfigs.additionalTraveler));
            this.additionalTravelerConfig.birthDate.editable = false;
            this.additionalTravelerConfig.age.show = true;
            this.additionalTravelerConfig.age.editable = false;
            this.additionalTravelerConfig.gender.required = false;
            this.additionalTravelerConfig.gender.show = false;
        }
    }

    private writePolicyHolderValues(purchaseData: PurchaseData, override: WriteValueOverride): void {
        // Copy trip cost, even if the rest of the form is not valid
        if (this.policyHolderForm.valid || override === WriteValueOverride.Override) {
            // Encompass rating engine requires a value for trip cost reguardless of product
            // AP does not collect the trip cost for AC product during this step
            // Instead of overwriting the value, update it if a new values is provided
            if (this.policyHolderForm.value.tripCost) {
                purchaseData.primaryTraveler.tripCost = this.policyHolderForm.value.tripCost;
            }

            // parse address to FormGroup because controls[] returns AbstractControl, need to access getRawValue()
            purchaseData.primaryTraveler.address = (<FormGroup>this.policyHolderForm.controls['address']).getRawValue();
            purchaseData.primaryTraveler.gender = this.policyHolderForm.value.gender;
        }
        else if (override === WriteValueOverride.SaveState) {
            this.sessionStorage.storeValue(this.policyHolderSessionKey, JSON.stringify(this.policyHolderForm.getRawValue()));
        }
    }

    private writePolicyBuyerValues(purchaseData: PurchaseData, override: WriteValueOverride): void {
        purchaseData.purchaseForOther = this.currentPurchaseData.purchaseForOther;

        if (purchaseData.purchaseForOther) {
            if (this.policyBuyerForm.valid || override === WriteValueOverride.Override) {
                purchaseData.policyBuyer = this.policyBuyerForm.getRawValue();
            }
            else if (override === WriteValueOverride.SaveState) {
                this.sessionStorage.storeValue(this.policyBuyerSessionKey, JSON.stringify(this.policyBuyerForm.getRawValue()));
            }
        }
        else {
            purchaseData.policyBuyer = null;
        }
    }

    private writeAdditionalTravelerValues(purchaseData: PurchaseData, override: WriteValueOverride): void {
        if (this.additionalTravelerForms.valid || override === WriteValueOverride.Override) {
            purchaseData.additionalTravelers =
                this.setIsPrimaryPropertyOnAdditionalTravelers(
                    this.additionalTravelerForms.getRawValue()
                );
        }
        else if (override === WriteValueOverride.SaveState) {
            this.sessionStorage.storeValue(
                this.additionalTravelerSessionKey,
                JSON.stringify(
                    this.setIsPrimaryPropertyOnAdditionalTravelers(
                        this.additionalTravelerForms.getRawValue()
                    )
                )
            );
        }
    }

    private setIsPrimaryPropertyOnAdditionalTravelers(travelers: Traveler[]): Traveler[] {
        if (travelers && travelers.length > 0) {
            travelers.forEach((t) => {
                t.isPrimary = false;
            });
        }

        return travelers;
    }

    private writeTripDetails(purchaseData: PurchaseData): void {
        let tripDetails: any = this.tripDetailsForm.getRawValue();

        if (tripDetails.tripDestination) {
            if (typeof tripDetails.tripDestination.destination === 'object' && tripDetails.tripDestination.destination && tripDetails.tripDestination.destination.name) {
                purchaseData.destinationCountry = tripDetails.tripDestination.destination;
                purchaseData.destination = tripDetails.tripDestination.destination.name;
            }

            purchaseData.destinationState = tripDetails.tripDestination.destinationState;
            purchaseData.destinationCity = tripDetails.tripDestination.destinationCity;
        }

        if (tripDetails.tripSuppliers) {
            let noAirlineChecked: boolean = tripDetails.tripSuppliers.airline.noAirline;
            let airline: string = TypeaheadHelper.extractTypeaheadValue(tripDetails.tripSuppliers.airline.airline);

            purchaseData.airline = (noAirlineChecked) ? null : airline;
            if (this.policyConfig.carRentalCompany.show) {
                purchaseData.carRentalCompany = TypeaheadHelper.extractTypeaheadValue(tripDetails.tripSuppliers.carRentalCompany);
            }
            if (this.policyConfig.cruiseLine.show) {
                purchaseData.cruiseLine = TypeaheadHelper.extractTypeaheadValue(tripDetails.tripSuppliers.cruiseLine);
            }
            if (this.policyConfig.hotel.show) {
                purchaseData.hotel = TypeaheadHelper.extractTypeaheadValue(tripDetails.tripSuppliers.hotel);
            }
            if (this.policyConfig.rentalCompany.show) {
                purchaseData.rentalCompany = TypeaheadHelper.extractTypeaheadValue(tripDetails.tripSuppliers.rentalCompany);
            }
            if (this.policyConfig.tourOperator.show) {
                purchaseData.tourOperator = TypeaheadHelper.extractTypeaheadValue(tripDetails.tripSuppliers.tourOperator);
            }
        }
    }

    private reQuoteOnPolicyHolderTripCostChange(newTripCost: number): void {
        let tripCostControl: AbstractControl = this.policyHolderForm.get('tripCost');
        let tripCostValid: boolean = tripCostControl ? tripCostControl.valid : false;

        if (tripCostValid && newTripCost !== this.currentPurchaseData.primaryTraveler.tripCost) {
            this.reQuoteData();
        }
    }

    /**
     * Requote when travelers are added/removed or when their trip cost changes
     */
    private reQuoteOnAdditionalTravelerChange(travelers: Traveler[]): void {
        if (travelers) {
            let shouldRequote: boolean =
                travelers.length !== this.currentPurchaseData.additionalTravelers.length ||
                this.additionalTravelerTripCostChanged(travelers);

            if (shouldRequote) {
                this.reQuoteData();
            }
        }
    }

    private reQuoteData() {
        this.quote(true, true, null).subscribe(quote => {
            this.tripCostChanged.emit(true);
        },
        null,
        null);
    }
    private additionalTravelerTripCostChanged(travelers: Traveler[]): boolean {
        let tripCostChanged: boolean = false;

        for (let i: number = 0; i < travelers.length; i++) {
            let formTraveler: Traveler = travelers[i];
            let currentTraveler: Traveler = this.currentPurchaseData.additionalTravelers[i];
            let travelerGroup: AbstractControl = this.additionalTravelerForms.controls[i];

            if (formTraveler && currentTraveler && travelerGroup) {
                let tripCostControl: AbstractControl = travelerGroup.get('tripCost');
                let tripCostValid: boolean = tripCostControl ? tripCostControl.valid : false;

                if (tripCostValid && formTraveler.tripCost !== currentTraveler.tripCost) {
                    tripCostChanged = true;
                    break;
                }
            }
        }

        return tripCostChanged;
    }

    private copyValue(obj: any): any {
        if (obj) {
            return JSON.parse(JSON.stringify(obj));
        }

        return null;
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./src/typescript/purchase/finish-quote-step.component.ts